import { Component, Input, OnInit, OnChanges, SimpleChanges, AfterViewInit, OnDestroy, ElementRef, ViewEncapsulation, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import { PageContent } from 'src/app/models/page-content.model';
import { ContentService } from 'src/app/services/content.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics.service';

@Component({
  selector: 'app-page-content',
  templateUrl: './page-content.component.html',
  styleUrls: ['./page-content.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PageContentComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() pageContent?: PageContent;
  @Input() pageId!: string;

  isLoading = false;

  private langChangeSubscription?: Subscription;

  constructor(
    private contentService: ContentService,
    private navigationService: NavigationService,
    private elementRef: ElementRef,
    private injector: Injector,
    private translateService: TranslateService,
    private googleAnalytics: GoogleAnalyticsService
  ) {}

  ngOnInit(): void {
      this.findMedianSortedArrays([1,2], [3]);
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      if (this.pageId) {
        this.loadContent();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageId'] && this.pageId) {
      this.loadContent();
    }
  }

  private loadContent(): void {
    this.isLoading = true;
    this.pageContent = undefined;
    this.contentService.getPageContent(this.pageId).subscribe(content => {
      this.pageContent = content;
      this.isLoading = false;
      setTimeout(() => { this.setupLinkTracking(); this.addZoomToImages(); this.embedVideos(); }, 100);
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupScrollMonitoring();
      this.addZoomToImages();
      this.setupLinkTracking();
      this.embedVideos();
    }, 100);
  }

  // Angular's DomSanitizer strips <iframe> out of [innerHTML], so content JSON carries a
  // placeholder div and the embed is built here after the content renders.
  //
  // The video id rides in a `yt-<id>` class token rather than a data attribute: the sanitizer
  // drops every attribute outside its allowlist, and data-* is not on it. `class` and
  // `aria-label` are, so those are the only two channels that survive into the DOM.
  private embedVideos(): void {
    const placeholders = this.elementRef.nativeElement.querySelectorAll('.section-content .video-embed, .subsection-content .video-embed');
    placeholders.forEach((placeholder: HTMLElement) => {
      const idClass = Array.from(placeholder.classList).find(name => name.startsWith('yt-'));
      const videoId = idClass?.substring('yt-'.length);
      if (!videoId || placeholder.querySelector('iframe')) {
        return; // nothing to embed, or already embedded by an earlier pass
      }

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}`;
      iframe.title = placeholder.getAttribute('aria-label') ?? '';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.setAttribute('frameborder', '0');
      placeholder.appendChild(iframe);
    });
  }

  private addZoomToImages(): void {
    const contentImages = this.elementRef.nativeElement.querySelectorAll('.section-content img');
    contentImages.forEach((img: HTMLImageElement) => {

      if (img.classList.contains('no-zoom')) {
        console.log('Zoom disabled for this image');
        return; // Skip zoom for images with no-zoom class
      }

      img.classList.add('app-zoomable-image');
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const appComponent = this.injector.get(AppComponent);
        appComponent.openImageModal(img.src, img.alt);
      });
    });
  }

  private setupLinkTracking(): void {
    const links = this.elementRef.nativeElement.querySelectorAll('.section-content a, .subsection-content a');
    links.forEach((anchor: HTMLAnchorElement) => {
      anchor.addEventListener('click', () => {
        const section = anchor.closest('[id]') as HTMLElement | null;
        this.googleAnalytics.trackEvent('content_link_click', {
          link_text: anchor.innerText.trim(),
          link_url: anchor.getAttribute('href') ?? '',
          section_id: section?.id ?? '',
          page_id: this.pageId,
        });
      });
    });
  }

  private setupScrollMonitoring(): void {
    const sections = this.elementRef.nativeElement.querySelectorAll('.content-section');
    if (sections.length === 0) return;
    
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px',
      threshold: 0
    };
    
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          this.navigationService.setCurrentSectionId(sectionId);
        }
      });
    };
    
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    sections.forEach((section: Element) => {
      observer.observe(section);
    });
  }
  
private findMedianSortedArrays(nums1: number[], nums2: number[]): number {
    // iterate one after another until we reach the median number between the two arrays

    let i: number = 0
    const length = nums1.length + nums2.length;
    const median = Math.floor((length / 2));

    //check if length is even then we get two numbers and average
    if(length % 2 == 0) {

    } else {
        // odd so we just get the number at index
        i = this.getNumberAtIndex(nums1, nums2, median);
    }

    console.log(i);
    return i
};

private getNumberAtIndex(arr1: number[], arr2:number[], target: number){
    const arrIndex:number[] = [0,0];
    let i = 0;

    while(i <= target){
        if(arr1[arrIndex[0]] < arr2[arrIndex[1]]) {
            if(i == target) {
                return arr1[i];
            }
            arrIndex[0]++;
        } else {
            if(i == target) {
                return arr2[i];
            }
            arrIndex[1]++;
        }
        i++;
    }

    return -1;
}
}
